import React, { createContext, useContext, useEffect, useState } from 'react';
import { api, setCustomerToken, getCustomerToken } from '../lib/api';
import { getVisitorId } from '../lib/tracker';

const CustomerAuthContext = createContext(null);

function formatError(detail) {
    if (detail == null) return 'Something went wrong. Please try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
    if (detail?.msg) return detail.msg;
    return String(detail);
}

export const CustomerAuthProvider = ({ children }) => {
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = getCustomerToken();
        if (!token) { setLoading(false); return; }
        api.get('/customers/me')
            .then((r) => setCustomer(r.data))
            .catch(() => setCustomerToken(null))
            .finally(() => setLoading(false));
    }, []);

    const register = async (email, password, name) => {
        try {
            const r = await api.post('/customers/register', { email, password, name, visitor_id: getVisitorId() });
            setCustomerToken(r.data.access_token);
            setCustomer(r.data.customer);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatError(e.response?.data?.detail) };
        }
    };

    const login = async (email, password) => {
        try {
            const r = await api.post('/customers/login', { email, password, visitor_id: getVisitorId() });
            setCustomerToken(r.data.access_token);
            setCustomer(r.data.customer);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatError(e.response?.data?.detail) };
        }
    };

    const logout = () => { setCustomerToken(null); setCustomer(null); };

    return (
        <CustomerAuthContext.Provider value={{ customer, loading, register, login, logout, isAuthed: !!customer }}>
            {children}
        </CustomerAuthContext.Provider>
    );
};

export const useCustomerAuth = () => {
    const ctx = useContext(CustomerAuthContext);
    if (!ctx) throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
    return ctx;
};
